import React, { useRef, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import DetailsCard from '../../../ui/layout/DetailsCard';
import ActionButton from '../../../ui/primitives/ActionButton';
import Loader from '../../../ui/primitives/Loader';
import AIInsightItem from './AIInsightItem';
import { getDashboardAIInsights } from '../../../modules/dashboard';
import QUERY_KEYS from '../../../query/queryKeys';

const AI_INSIGHTS_STALE_TIME = 30 * 60 * 1000; // 30 minutes

// const TARGET_ROUTES = {
//   testCases: (projectId) => `/project/${projectId}/testcases`,
//   issues: (projectId) => `/project/${projectId}/issues`,
// };

// const FALLBACK_ACTION_LABELS = {
//   testCases: 'View Test Cases',
//   issues: 'View Issues',
// };
// const DIRECT_FILTER_KEY_MAP = {
//   is_automated: 'isAutomated',
//   is_automation_needed: 'isAutomationNeeded',
//   priority: 'priority',
//   status: 'status',
//   labels: 'labels',
//   technology_layer: 'technologyLayer',
//   test_suites: 'testSuites',
//   module: 'module',
// };

// const mapActionFilters = (filters) => {
//   if (!filters || Object.keys(filters).length === 0) return null;
//   const mapped = {};
//   Object.entries(filters).forEach(([key, value]) => {
//     const mappedKey = DIRECT_FILTER_KEY_MAP[key];
//     if (mappedKey) mapped[mappedKey] = value;
//   });
//   return Object.keys(mapped).length > 0 ? mapped : null;
// };

const AIInsightsSection = ({ dateFilter }) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const forceRefreshRef = useRef(false);

  const queryKey = QUERY_KEYS.project.aiInsights(projectId, dateFilter);
  const [hasGenerated, setHasGenerated] = useState(() => {
    const state = queryClient.getQueryState(queryKey);
    if (!state?.dataUpdatedAt) return false;
    return Date.now() - state.dataUpdatedAt < AI_INSIGHTS_STALE_TIME;
  });

  const {
    data,
    dataUpdatedAt,
    isLoading: loading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      return getDashboardAIInsights(projectId, dateFilter, forceRefresh);
    },
    enabled: hasGenerated && !!projectId,
    staleTime: AI_INSIGHTS_STALE_TIME,
    gcTime: AI_INSIGHTS_STALE_TIME,
  });

  const insights = data?.insights || [];
  const isCacheStale = dataUpdatedAt ? Date.now() - dataUpdatedAt > AI_INSIGHTS_STALE_TIME : false;
  const showGenerateButton = !hasGenerated || (isCacheStale && !loading && !isFetching);

  const handleGenerate = () => {
    if (hasGenerated) {
      refetch();
    } else {
      setHasGenerated(true);
    }
  };

  const handleRefresh = () => {
    forceRefreshRef.current = true;
    refetch();
  };

  // Commented out to hide per-insight action button behavior in the UI.
  /*
  const handleInsightAction = (insight) => {
    const target = insight.action?.target;
    const routeBuilder = TARGET_ROUTES[target];
    if (!routeBuilder) return;

    const mappedFilters = mapActionFilters(insight.action?.filters);
    navigate(
      routeBuilder(projectId),
      mappedFilters ? { state: { filters: mappedFilters } } : undefined
    );
  };
  */

  if (showGenerateButton) {
    return (
      <DetailsCard title="AI Insights & Recommendations">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            py: 5,
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            Generate AI-powered insights and recommendations based on your current repository data.
          </Typography>
          <ActionButton
            label="Generate AI Insights"
            icon={<AutoAwesomeIcon />}
            intent="primary"
            onClick={handleGenerate}
          />
        </Box>
      </DetailsCard>
    );
  }

  if (loading || isFetching) {
    return (
      <DetailsCard title="AI Insights & Recommendations">
        <Loader minHeight={200} />
      </DetailsCard>
    );
  }

  if (isError) {
    return (
      <DetailsCard title="AI Insights & Recommendations">
        <Alert severity="error" action={<ActionButton label="Retry" onClick={() => refetch()} />}>
          {error?.message || 'Failed to generate AI insights.'}
        </Alert>
      </DetailsCard>
    );
  }

  return (
    <DetailsCard
      title="AI Insights & Recommendations"
      actions={
        <ActionButton
          label="Refresh"
          icon={<RefreshIcon />}
          variant="text"
          color="primary"
          onClick={handleRefresh}
        />
      }
    >
      {insights.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No insights available for the selected period.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'auto minmax(0, 1fr) 240px auto' },
            columnGap: { sm: 4 },
            alignItems: 'start',
          }}
        >
          {insights.map((insight, index) => {
            return (
              <AIInsightItem
                key={`${insight.title}-${index}`}
                priority={insight.priority?.toLowerCase()}
                title={insight.title}
                description={insight.description}
                recommendation={insight.recommendation}
                impact={insight.impact}
                // Commented out to hide the action button on each insight card
                // actionLabel={insight.action?.label}
                // onActionClick={() => handleInsightAction(insight)}
                isLast={index === insights.length - 1}
              />
            );
          })}
        </Box>
      )}
    </DetailsCard>
  );
};

export default AIInsightsSection;